import Panzoom from "@panzoom/panzoom/dist/panzoom.es.js";
import { VERBOSE } from "../config.js";

const MIN_SCALE = 1;
const DOUBLE_CLICK_SCALE = 2;
const NEUTRAL_TOUCH_ACTION = "pan-y";
const ZOOMED_TOUCH_ACTION = "none";
const DOUBLE_CLICK_PAGE_SELECTOR = ".rhea-page__interior";
const DOUBLE_CLICK_NAVIGATION_SELECTOR = [
  ".rhea-page-navigation",
  ".splide__arrow",
  ".splide__pagination",
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  '[role="button"]',
  '[role="link"]',
].join(", ");

const roundScale = (scale) => Math.round(scale * 1000) / 1000;
const clamp = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(value, maximum));

export function calculateClampedPan({ pan, elementRect, viewportRect, scale }) {
  if (!Number.isFinite(scale) || scale <= 0) {
    throw new RangeError("Pan bounds require a finite positive scale.");
  }

  const maxPanX =
    Math.max(0, (elementRect.width - viewportRect.width) / 2) / scale;
  const maxPanY =
    Math.max(0, (elementRect.height - viewportRect.height) / 2) / scale;

  return {
    x: maxPanX === 0 ? 0 : clamp(pan.x, -maxPanX, maxPanX),
    y: maxPanY === 0 ? 0 : clamp(pan.y, -maxPanY, maxPanY),
  };
}

/**
 * Applies a persistent zoom transform to the book around the Splide slider.
 * Splide remains responsible for changing pages on its nested list element.
 */
export default class PanZoomController {
  static zoomStartedEvent = "panzoomcontroller:zoomstarted";
  static zoomChangedEvent = "panzoomcontroller:zoomchanged";
  static zoomEndedEvent = "panzoomcontroller:zoomended";
  static pinchStartedEvent = "panzoomcontroller:pinchstarted";
  static pinchEndedEvent = "panzoomcontroller:pinchended";

  #container;
  #element;
  #pointerElement;
  #panzoom;
  #resizeObserver;
  #scale = MIN_SCALE;
  #snappingToMinimum = false;
  #pinching = false;
  #doubleClickZoomPending = false;
  #wheelBoundsRefreshPending = false;
  #wheelStep;
  #suspendedTransform;
  #verbose;

  constructor({
    container,
    element,
    pointerElement,
    pinchStep,
    wheelZoom = 4 / 3,
    maxScale = 10,
    verbose = false,
    panzoomFactory = Panzoom,
  }) {
    this.#container = container;
    this.#element = element;
    this.#pointerElement = pointerElement;
    this.maxScale = maxScale;
    this.#wheelStep = 3 * Math.log(wheelZoom);
    this.#verbose = verbose || VERBOSE;

    if (this.#verbose) console.log("PanZoomController: create", this);

    if (this.#element.parentElement !== this.#pointerElement) {
      throw new TypeError(
        "PanZoomController requires pointerElement to be the direct parent of element.",
      );
    }

    this.#panzoom = panzoomFactory(this.#element, {
      canvas: true,
      contain: false,
      maxScale: this.maxScale,
      minScale: MIN_SCALE,
      panOnlyWhenZoomed: true,
      pinchAndPan: true,
      step: pinchStep - 1,
      touchAction: NEUTRAL_TOUCH_ACTION,
      handleStartEvent: (event) => {
        if (!this.isZoomed()) return;

        event.preventDefault();
        event.stopPropagation();
      },
    });

    this.#addEventListeners();
  }

  get scale() {
    return this.#scale;
  }

  isZoomed() {
    return this.#scale > MIN_SCALE;
  }

  isPinching() {
    return this.#pinching;
  }

  zoomBy(multiplier) {
    if (this.#verbose) console.log("PanZoomController: zoomBy", multiplier);
    if (!this.isZoomed() && multiplier < 1) return this.#scale;

    const targetScale = clamp(
      roundScale(this.#panzoom.getScale() * multiplier),
      MIN_SCALE,
      this.maxScale,
    );
    const result = this.#panzoom.zoom(targetScale, { animate: true });
    this.#synchronizeScale(result.scale);

    return this.#scale;
  }

  zoomWithWheel(event) {
    if (this.#verbose)
      console.log("PanZoomController: zoomWithWheel", event.deltaY);
    this.#wheelBoundsRefreshPending = true;
    const result = this.#panzoom.zoomWithWheel(event, {
      step: this.#wheelStep,
    });
    this.#synchronizeScale(result.scale);

    return this.#scale;
  }

  reset() {
    if (this.#verbose) console.log("PanZoomController: reset");
    if (!this.isZoomed()) return false;

    this.#panzoom.reset({ animate: false });
    this.#updateScale(MIN_SCALE);

    return true;
  }

  /**
   * Keeps the current scale and clamps the pan position after page or layout
   * changes. Intentional gutters around a centered book remain untouched.
   */
  refreshBounds() {
    if (!this.isZoomed()) return;

    const pan = this.#panzoom.getPan();
    const elementRect = this.#element.getBoundingClientRect();
    const viewportRect = this.#pointerElement.getBoundingClientRect();
    const clampedPan = calculateClampedPan({
      pan,
      elementRect,
      viewportRect,
      scale: this.#scale,
    });

    this.#panzoom.pan(clampedPan.x, clampedPan.y, {
      animate: false,
      force: true,
    });
  }

  /**
   * Temporarily removes the visual transform without changing Panzoom's
   * internal scale or pan state. Splide can then measure its unscaled slides.
   */
  suspendTransform() {
    if (!this.isZoomed() || this.#suspendedTransform) return false;

    this.#suspendedTransform = {
      transform: this.#element.style.transform,
      transition: this.#element.style.transition,
      visibility: this.#element.style.visibility,
    };

    this.#element.style.visibility = "hidden";
    this.#element.style.transition = "none";
    this.#element.style.transform = "none";

    // Force layout now so Splide's following measurements cannot be
    // coalesced with restoring the transform.
    this.#element.getBoundingClientRect();

    return true;
  }

  restoreTransform() {
    if (!this.#suspendedTransform) return false;

    const { transform, transition, visibility } = this.#suspendedTransform;
    this.#element.style.transform = transform;
    this.#element.style.transition = transition;
    this.#element.style.visibility = visibility;
    this.#suspendedTransform = undefined;

    this.refreshBounds();
    return true;
  }

  destroy() {
    if (this.#verbose) console.log("PanZoomController: destroy");

    this.restoreTransform();
    this.#panzoom.destroy();
    this.#element.removeEventListener(
      "panzoomchange",
      this.#handlePanzoomChange,
    );
    this.#element.removeEventListener("panzoomstart", this.#handlePanzoomStart);
    this.#element.removeEventListener("panzoomend", this.#handlePanzoomEnd);
    this.#element.removeEventListener("dblclick", this.#handleDoubleClick);
    this.#resizeObserver?.disconnect();
    window.removeEventListener("resize", this.#handleResize);
  }

  #updateScale(scale) {
    const previousScale = this.#scale;
    if (scale === previousScale) return;

    this.#scale = scale;
    const wasZoomed = previousScale > MIN_SCALE;
    const isZoomed = scale > MIN_SCALE;
    if (isZoomed !== wasZoomed) {
      this.#panzoom.setOptions({
        touchAction: isZoomed ? ZOOMED_TOUCH_ACTION : NEUTRAL_TOUCH_ACTION,
      });
    }
    const detail = {
      scale,
      previousScale,
      atMinimum: scale <= MIN_SCALE,
      atMaximum: scale >= this.maxScale,
    };

    this.#container.dispatchEvent(
      new CustomEvent(PanZoomController.zoomChangedEvent, { detail }),
    );

    if (isZoomed && !wasZoomed) {
      this.#container.classList.add("rhea-zoomed");
      this.#container.dispatchEvent(
        new CustomEvent(PanZoomController.zoomStartedEvent, { detail }),
      );
    } else if (!isZoomed && wasZoomed) {
      // A scale of 1 is the neutral, centered viewer state. Panzoom keeps
      // its last translation when only the scale changes, so explicitly
      // clear that translation before handing dragging back to Splide.
      this.#panzoom.pan(0, 0, { animate: false, force: true });
      this.#container.classList.remove("rhea-zoomed");
      this.#container.dispatchEvent(
        new CustomEvent(PanZoomController.zoomEndedEvent, { detail }),
      );
    }
  }

  #synchronizeScale(scale) {
    const roundedScale = roundScale(scale);

    // Panzoom bases panOnlyWhenZoomed on its unrounded internal scale. A
    // value such as 1.0004 previously looked neutral to this controller
    // while Panzoom still treated it as zoomed and moved the whole book.
    // Snap both states to the exact neutral transform at that boundary.
    if (
      roundedScale <= MIN_SCALE &&
      scale > MIN_SCALE &&
      !this.#snappingToMinimum
    ) {
      this.#snappingToMinimum = true;
      try {
        this.#panzoom.zoom(MIN_SCALE, { animate: false, force: true });
      } finally {
        this.#snappingToMinimum = false;
      }
      this.#updateScale(MIN_SCALE);
      return;
    }

    this.#updateScale(roundedScale);
  }

  #handlePanzoomChange = (event) => {
    const pan = this.#panzoom.getPan();
    const currentScale = roundScale(this.#panzoom.getScale());
    const isCurrentTransform =
      roundScale(event.detail.scale) === currentScale &&
      Math.abs(event.detail.x - pan.x) < 0.001 &&
      Math.abs(event.detail.y - pan.y) < 0.001;
    // Panzoom applies every queued transform in requestAnimationFrame.
    // Ignore callbacks for superseded wheel/double-click states.
    if (!isCurrentTransform) return;

    this.#synchronizeScale(event.detail.scale);
    if (this.#doubleClickZoomPending && event.detail.scale > MIN_SCALE) {
      this.#doubleClickZoomPending = false;
    }
    if (this.#wheelBoundsRefreshPending) {
      this.#wheelBoundsRefreshPending = false;
      this.refreshBounds();
    }
  };

  #handlePanzoomStart = (event) => {
    if (this.#pinching || event.detail.originalEvent?.isPrimary !== false)
      return;

    this.#pinching = true;
    this.#container.dispatchEvent(
      new CustomEvent(PanZoomController.pinchStartedEvent),
    );
  };

  #handlePanzoomEnd = () => {
    this.refreshBounds();
    if (!this.#pinching) return;

    this.#pinching = false;
    this.#container.dispatchEvent(
      new CustomEvent(PanZoomController.pinchEndedEvent),
    );
  };

  #handleDoubleClick = (event) => {
    const target = event.target;
    if (
      !target?.closest?.(DOUBLE_CLICK_PAGE_SELECTOR) ||
      target.closest(DOUBLE_CLICK_NAVIGATION_SELECTOR)
    )
      return;

    event.preventDefault();
    if (this.#doubleClickZoomPending) return;

    if (this.isZoomed()) {
      this.reset();
      return;
    }

    this.#doubleClickZoomPending = true;
    const result = this.#panzoom.zoomToPoint(DOUBLE_CLICK_SCALE, event, {
      animate: true,
    });
    this.#synchronizeScale(result.scale);
  };

  #handleResize = () => {
    this.refreshBounds();
  };

  #addEventListeners() {
    this.#element.addEventListener("panzoomchange", this.#handlePanzoomChange);
    this.#element.addEventListener("panzoomstart", this.#handlePanzoomStart);
    this.#element.addEventListener("panzoomend", this.#handlePanzoomEnd);
    this.#element.addEventListener("dblclick", this.#handleDoubleClick);

    if ("ResizeObserver" in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this.#pointerElement);
      this.#resizeObserver.observe(this.#element);
    } else {
      window.addEventListener("resize", this.#handleResize);
    }
  }
}
